import React, { useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { CtyButton, useAgGridStatePersistence, getTableState } from "@circuitry-ai/doc-ui";
import { AgGridReact } from 'ag-grid-react';
import { ItemType, getCellContent, getRelationBadgeClass, TYPE_CONFIG } from '../fga-utils';

// Generic cell renderer
export const GenericCellRenderer = ({ type, data, field }: { type: ItemType; data: any; field: string }) => {
  const content = getCellContent(type, data, field);

  return (
    <div className="flex items-center h-full gap-2">
      <span className="text-sm font-medium text-foreground">{content.text}</span>
    </div>
  );
};

export const RelationCellRenderer = ({ relation }: { relation: string }) => {
  const badgeClass = getRelationBadgeClass(relation);
  
  return (
    <div className="flex items-center h-full">
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
        {relation}
      </span>
    </div>
  );
};

// Shared action button component
export const ActionButton = React.memo(({ onPress, label = '', isLoading = false, isDisabled = false }: { 
  onPress: () => void; 
  label?: string; 
  isLoading?: boolean; 
  isDisabled?: boolean;
}) => (
  <CtyButton 
    size="sm" 
    color="danger" 
    variant="light" 
    onPress={onPress} 
    isLoading={isLoading} 
    isDisabled={isDisabled || isLoading}
    startContent={isLoading ? undefined : <Icon icon="hugeicons:delete-02" width={16} height={16} />}
  >
    {isLoading ? 'Removing...' : label}
  </CtyButton>
));

// Shared search and chips component
export const SearchAndChips = React.memo(({ 
  type, 
  searchTerm, 
  setSearchTerm, 
  filteredResults, 
  selectedItems, 
  onAddItem, 
  onRemoveItem, 
  onUpdateItems, 
  isAdding, 
  title, 
  description, 
  placeholder 
}: { 
  type: ItemType;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filteredResults: any[];
  selectedItems: any[];
  onAddItem: (item: any) => void;
  onRemoveItem: (item: any) => void;
  onUpdateItems: () => void;
  isAdding: boolean;
  title: string;
  description: string;
  placeholder: string;
}) => {
  const config = TYPE_CONFIG[type];

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold">{title}</h2>
          <p className="text-default-500">{description}</p>
        </div>
      </div>
      <div className="relative">
        <input 
          type="text" 
          placeholder={placeholder} 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
        <Icon icon="mdi:magnify" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-default-400" width={20} height={20} />
      </div>

      {searchTerm.trim() && (
        <div className="mt-2 bg-content1 border border-default-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredResults.length > 0 ? (
            filteredResults.slice(0, 10).map((item: any) => (
              <button 
                key={`${type}-${item.user_id || item.resource}-${item.relation || ''}`}
                className="w-full text-left px-4 py-2 hover:bg-default-100 cursor-pointer border-b border-default-200 last:border-b-0"
                onClick={() => onAddItem(item)} 
                onKeyDown={(e) => e.key === 'Enter' && onAddItem(item)}
              >
                <div className="flex items-center gap-2">
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {(() => {
                        if (type === 'users') {
                          return item.name || item.email;
                        }
                        return item.resource.includes('/') ? item.resource.split('/')[1] : item.resource;
                      })()}
                    </div>
                    <div className="text-xs text-default-500">
                      {type === 'users' ? item.email : item.relation}
                    </div>
                  </div>
                </div>
              </button>
            ))
          ) : (
            <div className="px-4 py-6 text-center text-default-500">
              <Icon icon="mdi:magnify" className="mx-auto mb-2 text-default-300" width={24} height={24} />
              <div className="text-sm font-medium text-default-400">No matching results found</div>
              <div className="text-xs text-default-300 mt-1">Try different keywords or check if all {type} are already added</div>
            </div>
          )}
        </div>
      )}

      {/* Chips section */}
      <div className="mt-4">
        <div className="flex items-start justify-between">
          <div className="flex flex-wrap gap-2 flex-1">
            {selectedItems.map((item: any, index: number) => (
              <div 
                key={`${type}-${index}`} 
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${config.bgColor} ${config.textColor}`}
              >
                <span>
                  {(() => {
                    if (type === 'users') {
                      return item.name;
                    }
                    return item.resource?.includes('/') ? item.resource.split('/')[1] : item.resource;
                  })()}
                </span>
                <span className="text-xs">
                  ({type === 'users' ? item.email : item.relation})
                </span>
                <button 
                  onClick={() => onRemoveItem(item)} 
                  className={`ml-1 ${config.hoverColor}`}
                >
                  <Icon icon="mdi:close" width={14} height={14} />
                </button>
              </div>
            ))}
          </div>

          {selectedItems.length > 0 && (
            <CtyButton 
              size="sm" 
              color="primary" 
              variant="solid" 
              onPress={onUpdateItems} 
              isLoading={isAdding} 
              isDisabled={isAdding}
              startContent={isAdding ? undefined : <Icon icon="mdi:plus" width={16} height={16} />}
            >
              {isAdding ? 'Adding...' : `Add ${type.charAt(0).toUpperCase() + type.slice(1)}`}
            </CtyButton>
          )}
        </div>
      </div>
    </div>
  );
});

const SETTINGS_AG_GRID_WRAPPER_TABLE_ID = 'settings-ag-grid-wrapper-table';

// Shared AG Grid wrapper component
export const AgGridWrapper = React.memo(({ 
  rowData, 
  columnDefs, 
  children 
}: { 
  rowData: any[]; 
  columnDefs: any[]; 
  children?: React.ReactNode; 
}) => {
  const { augmentGridReady, mergeColumnDefs, suppressDragLeaveHidesColumns } =
    useAgGridStatePersistence(SETTINGS_AG_GRID_WRAPPER_TABLE_ID);
  const columnDefsWithSort = useMemo(() => mergeColumnDefs(columnDefs), [columnDefs, mergeColumnDefs]);
  const onGridReady = useCallback((params: any) => {
    augmentGridReady(params, (p) => {
      if (!getTableState(SETTINGS_AG_GRID_WRAPPER_TABLE_ID)?.columnState?.length) {
        p?.api?.sizeColumnsToFit?.();
      }
    });
  }, [augmentGridReady]);

  return (
    <div className="w-full py-6">
      {children}
      <div 
        className="ag-theme-alpine" 
        style={{ 
          height: "100%", 
          width: "100%", 
          '--ag-border-color': '#e0e0e0', 
          '--ag-cell-horizontal-border': '1px solid var(--ag-border-color)' 
        } as React.CSSProperties}
      >
        <AgGridReact 
          suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
          rowData={rowData} 
          columnDefs={columnDefsWithSort} 
          defaultColDef={{ resizable: true }} 
          headerHeight={40} 
          domLayout="autoHeight"
          onGridReady={onGridReady} 
          pagination={true} 
          paginationPageSize={10} 
          paginationPageSizeSelector={[10, 20, 40, 50]} 
          alwaysShowHorizontalScroll={true} 
        />
      </div>
    </div>
  );
});
